import { jwtDecode } from 'jwt-decode';

export interface Permission {
    description: string;
    name: string;
    permission_id: number;
    type: string;
}

export interface AccessTokenClaims {
    aud: string;
    check_employees: boolean;
    client: string;
    client_id: string;
    exp: number;
    extra_employees: number[];
    iat: number | string;
    iss: string;
    jti: string;
    nbf: number;
    permissions_hex: string;
    profile: string;
    profile_id: number | string;
    scope: string[];
    sub: number | string;
    tags: number[];
    user_id: number;
    username: string;
    views: string[];
}

export interface AuthorizationState {
    claims: AccessTokenClaims;
    permissions: string[];
    scopes: string[];
    views: string[];
    tagIds: string[];
    extraEmployeeIds: string[];
}

export const EMPTY_AUTHORIZATION: AuthorizationState | null = null;

const VIEW_ALIASES: Record<string, string> = {
    workers: 'employees',
    schedule: 'scheduler',
    results_report: 'resume',
    execute_report: 'execute_reports',
};

const VIEW_PATH_ALIASES: Record<string, string> = {
    '/reports': '/custom_reports',
};

const normalizeView = (view: string) => {
    const [rawPath, query = ''] = view.split('?');
    const path = VIEW_PATH_ALIASES[rawPath] ?? rawPath;
    const params = new URLSearchParams(query);
    const subpage = params.get('subpage') ?? params.get('view');
    const normalizedSubpage = subpage ? (VIEW_ALIASES[subpage] ?? subpage) : null;

    return normalizedSubpage ? `${path}?subpage=${normalizedSubpage}` : path;
};

export const decodePermissionsHex = (permissionsHex: string): number[] => {
    const binary = BigInt(`0x${permissionsHex || '0'}`).toString(2);

    return [...binary].map((bit, index) => (bit === '1' ? index + 1 : null)).filter((permissionId): permissionId is number => permissionId !== null);
};

export const resolvePermissions = (permissionsHex: string, permissionCatalog: Permission[]): string[] => {
    const permissionIds = decodePermissionsHex(permissionsHex);

    return permissionIds.flatMap((permissionId) => {
        const permission = permissionCatalog.find((item) => item.permission_id === permissionId);

        return permission ? [permission.name] : [];
    });
};

export const normalizeAuthorization = (claims: AccessTokenClaims, permissionCatalog: Permission[]): AuthorizationState => ({
    claims,
    permissions: resolvePermissions(claims.permissions_hex, permissionCatalog),
    scopes: claims.scope ?? [],
    views: (claims.views ?? []).map(normalizeView),
    tagIds: (claims.tags ?? []).map(String),
    extraEmployeeIds: (claims.extra_employees ?? []).map(String),
});

export const decodeAuthorization = (accessToken: string, permissionCatalog: Permission[]): AuthorizationState => {
    const claims = jwtDecode<AccessTokenClaims>(accessToken);
    return normalizeAuthorization(claims, permissionCatalog);
};

export const hasPermission = (authorization: AuthorizationState | null, permissions: string[]) =>
    permissions.length > 0 && permissions.every((permission) => authorization?.permissions.includes(permission) ?? false);

export const hasScope = (authorization: AuthorizationState | null, scope: string) => authorization?.scopes.includes(scope) ?? false;

export const canView = (authorization: AuthorizationState | null, view: string) => authorization?.views.includes(normalizeView(view)) ?? false;

export const hasTagAccess = (authorization: AuthorizationState | null, tagId: string | number) => authorization?.tagIds.includes(String(tagId)) ?? false;

export { normalizeView };
